import { z } from "zod";

/*
 * Building blocks every contract uses (07 §1 principles K-06 to K-08, 06 §1 D-01 to D-06).
 * Browser-safe: no node imports, no environment.
 */

/** Keys are UUIDs, version 7 in the database (D-01). */
export const Uuid = z.uuid();

/** Timestamps are ISO 8601 in UTC (K-07, D-02). */
export const IsoDateTime = z.iso.datetime();

/** Calendar dates, e.g. the usage day OpenRouter reports (`usage_date`). */
export const IsoDate = z.iso.date();

/** Billing period, the calendar month `YYYY-MM` (02 Abrechnungsperiode, 06 `period`). */
export const Period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "period must be YYYY-MM");
export type Period = z.infer<typeof Period>;

/** ISO 4217 code. The ledger is USD (ADR-058); display prices carry their own currency (ADR-063). */
export const Currency = z.string().regex(/^[A-Z]{3}$/, "currency must be a three-letter ISO code");
export type Currency = z.infer<typeof Currency>;

/**
 * The currency of the ledger and of every limit: monthly limits, demo limits, the sum rule R-04
 * (ADR-058). Product rows and entitlements store limits without a currency of their own.
 */
export const LEDGER_CURRENCY = "USD";

/** The range of PostgreSQL's `bigint`, the column type of every amount (06 D-03). */
const BIGINT_MIN = -(2n ** 63n);
const BIGINT_MAX = 2n ** 63n - 1n;

/**
 * An integer amount in micro units of its currency as a string (K-06, D-03): 1 USD = "1000000".
 * Strings, because JS numbers lose precision and JSON has no bigint. `packages/core` parses them
 * with `parseMicros`. Bounded to the database's `bigint`, so an amount beyond is
 * `400 validation_failed`, never a database error (N4 of the review 2026-10-19).
 */
export const Micros = z
  .string()
  .regex(/^-?\d+$/, "micros must be an integer string")
  .refine(
    // Zod 4 runs this after a failed regex too, so it checks the form again: BigInt would throw
    // on "1.5" out of safeParse, a 500 instead of a 400 (tests/common.test.ts)
    (value) => !/^-?\d+$/.test(value) || (BigInt(value) >= BIGINT_MIN && BigInt(value) <= BIGINT_MAX),
    "micros must fit a 64-bit integer (06 D-03)",
  );
export type Micros = z.infer<typeof Micros>;

/** Money as it travels across the API: `{ micros: "1234567", currency: "USD" }` (K-06). */
export const Money = z.object({
  micros: Micros,
  currency: Currency,
});
export type Money = z.infer<typeof Money>;

/** Money that cannot be negative: limits, prices, credit limits. */
export const NonNegativeMoney = Money.extend({
  micros: Micros.regex(/^\d+$/, "amount must not be negative"),
});
export type NonNegativeMoney = z.infer<typeof NonNegativeMoney>;

/** Money that must be above zero: a credit limit at approval (R-03), a correction amount. */
export const PositiveMoney = Money.extend({
  micros: Micros.regex(/^0*[1-9]\d*$/, "amount must be positive"),
});

/** Token counts from OpenRouter. Counts, not money, so plain integers. */
export const Count = z.number().int().nonnegative();

/**
 * A share as a ratio the API computed (ADR-158 point F12.2-10): `0.5` is half, `1` all of it; not
 * capped at 1, so usage beyond a limit shows as it is. Rounded half up to four decimal places
 * (0.01 %), so the portal formats it as a percentage or a bar width without computing it.
 */
export const Share = z.number().nonnegative();
export type Share = z.infer<typeof Share>;

/**
 * How the month's budget stands (ADR-158 point F12.2-10, sub-point 3): `insufficient` ("Reicht
 * nicht") when the forecast exceeds the sum of the monthly limits, `tight` ("Knapp") above 90 % of
 * it, else `ok` ("Im Rahmen").
 */
export const BudgetState = z.enum(["ok", "tight", "insufficient"]);
export type BudgetState = z.infer<typeof BudgetState>;

/** Free text that must say something: reasons, messages, notes. */
export const Reason = z.string().trim().min(1).max(1000);

/**
 * An address the portal shows as a link: https only. `javascript:` or `data:` in an `href` runs
 * in the portal's origin, `http:` leaks the session's next request (M15 of the review 2026-10-19).
 */
export const HttpsUrl = z.url({ protocol: /^https$/ });

export const PAGE_SIZE_DEFAULT = 50;
export const PAGE_SIZE_MAX = 200;

/** Query of every list route (K-08): `?page=1&size=50`, size at most 200. Coerces from strings. */
export const PageQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export type PageQuery = z.infer<typeof PageQuery>;

/** Response of every list route: the page, its position and the total count. */
export function paged<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int().min(1),
    size: z.number().int().min(1).max(PAGE_SIZE_MAX),
    total: z.number().int().nonnegative(),
  });
}
export type Paged<T> = { items: T[]; page: number; size: number; total: number };

/*
 * States and kinds, named after 02 §3 and the enums in 06 (D-06). A new value is a migration
 * and a contract change in one go.
 */

export const CustomerType = z.enum(["organization", "private"]);
export type CustomerType = z.infer<typeof CustomerType>;

export const CustomerStatus = z.enum(["registered", "approved", "suspended"]);
export type CustomerStatus = z.infer<typeof CustomerStatus>;

/** Roles of a user at their customer (ADR-034). From the instance: `external_user` → administrator, `guest` → user. */
export const MembershipRole = z.enum(["administrator", "user"]);
export type MembershipRole = z.infer<typeof MembershipRole>;

/** Whether the role follows the instance or was set by hand, which then wins (ADR-041). */
export const RoleSource = z.enum(["instance", "manual"]);
export type RoleSource = z.infer<typeof RoleSource>;

/** Staff roles, separate from any customer role (ADR-065, Grundprinzip 9). */
export const StaffRole = z.enum(["support", "admin"]);
export type StaffRole = z.infer<typeof StaffRole>;

export const UserStatus = z.enum(["active", "blocked", "pseudonymized"]);
export type UserStatus = z.infer<typeof UserStatus>;

export const BillingModel = z.enum(["one_time", "one_time_subscription", "subscription", "demo"]);
export type BillingModel = z.infer<typeof BillingModel>;

export const ProviderType = z.enum(["internal", "manual", "ai_cockpit"]);
export type ProviderType = z.infer<typeof ProviderType>;

export const OrderStatus = z.enum(["received", "in_progress", "completed", "cancelled"]);
export type OrderStatus = z.infer<typeof OrderStatus>;

export const EntitlementKind = z.enum(["standard", "demo"]);
export type EntitlementKind = z.infer<typeof EntitlementKind>;

/**
 * `pending`, `active` and `paused` are living; `expired` ends a demo's period, `revoked` a
 * cancelled order or staff by hand (UC-17), `cancelled` support's termination of the
 * subscription (UC-16, ADR-137 point 5).
 */
export const EntitlementStatus = z.enum(["pending", "active", "paused", "expired", "revoked", "cancelled"]);
export type EntitlementStatus = z.infer<typeof EntitlementStatus>;

export const ProvisioningStatus = z.enum(["not_needed", "waiting", "running", "succeeded", "failed"]);
export type ProvisioningStatus = z.infer<typeof ProvisioningStatus>;

export const ResourceState = z.enum(["active", "paused", "removed"]);
export type ResourceState = z.infer<typeof ResourceState>;

export const TaskKind = z.enum(["provision", "remove", "verify_link", "other"]);
export type TaskKind = z.infer<typeof TaskKind>;

export const TaskStatus = z.enum(["open", "done", "cancelled"]);
export type TaskStatus = z.infer<typeof TaskStatus>;

/**
 * What an inquiry is about (ADR-093): `access` asks for access or a product, from the website or
 * a guest in the portal; `support` (a question or a problem), `callback` and `workflow` come from
 * a customer's session. `workflow` is handled like `support` in the alpha release. `application`
 * is the application for the beta from the website's form (ADR-143, addendum to
 * ADR-093 and ADR-133 point 1).
 */
export const InquiryKind = z.enum(["access", "support", "callback", "workflow", "application"]);
export type InquiryKind = z.infer<typeof InquiryKind>;

export const InquirySource = z.enum(["website", "portal"]);
export type InquirySource = z.infer<typeof InquirySource>;

export const InquiryStatus = z.enum(["new", "forwarded", "closed"]);
export type InquiryStatus = z.infer<typeof InquiryStatus>;

/** `credit` and `expiry` exist in the enum for v1 (ADR-024) and never occur in the alpha release. */
export const LedgerType = z.enum(["usage", "correction", "invoice", "credit", "expiry"]);
export type LedgerType = z.infer<typeof LedgerType>;

export const AiKeyStatus = z.enum(["active", "revoked", "rotated"]);
export type AiKeyStatus = z.infer<typeof AiKeyStatus>;

export const ActorType = z.enum(["user", "staff", "system"]);
export type ActorType = z.infer<typeof ActorType>;
